// Publish a chromium build (data-confident layout).
//
// Mirrors the profiles publish page: a left source rail with the
// mode picker and per-mode inputs, a right hero inspection panel
// keyed on the resolved version, and a sticky commit bar that
// materialises once we have a version. Three input modes:
//   1. From catalog  — Combobox over the chromium catalog mirror
//      (default when populated). Already-published versions are
//      disabled in the dropdown.
//   2. Latest upstream — publish the newest catalog version.
//      Disabled when the resolved latest is already in the manifest.
//   3. Advanced (manual) — free-text version. Hidden behind a
//      "Show advanced" disclosure to discourage typos.
//
// Both the UI and the API enforce "no double-publish": the submit
// button disables when the resolved version is already in the
// manifest, and the backend returns 409 ALREADY_PUBLISHED if the
// guard is bypassed.

import { zodResolver } from "@hookform/resolvers/zod";
import { FullVersionRegex } from "@schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatRelative } from "@veil/ui/lib/format";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useLocation, useSearch } from "wouter";
import { z } from "zod";
import { CatalogFreshnessStrip } from "@/components/chromium/catalog-freshness-strip";
import { CommitBar } from "@/components/chromium/publish-chromium/commit-bar";
import {
	type ModeOption,
	type PublishChromiumMode,
	SourceRail,
} from "@/components/chromium/publish-chromium/source-rail";
import {
	HeroEmpty,
	VersionHero,
} from "@/components/chromium/publish-chromium/version-hero";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";
import { useChromiumUpstream } from "@/lib/releases/hooks.ts";
import type { ManifestResponse } from "@/lib/releases/types.ts";

const EASE = [0.22, 1, 0.36, 1] as const;

const PublishChromiumFormSchema = z
	.object({
		mode: z.enum(["latest", "catalog", "manual"]),
		version: z.string().optional(),
	})
	.refine(
		(data) =>
			data.mode === "latest" ||
			(typeof data.version === "string" && FullVersionRegex.test(data.version)),
		{ message: "Must look like 147.0.7727.139", path: ["version"] },
	);

type PublishChromiumForm = z.infer<typeof PublishChromiumFormSchema>;

export function ReleaseNewChromium() {
	const { user } = useAuth();
	const canSync = hasRole(user, "operator");
	const reduce = useReducedMotion();

	const [, navigate] = useLocation();
	const search = useSearch();
	const presetVersion = useMemo(() => {
		const params = new URLSearchParams(search);
		const v = params.get("version") ?? "";
		return FullVersionRegex.test(v) ? v : "";
	}, [search]);

	const queryClient = useQueryClient();
	const upstreamQuery = useChromiumUpstream();
	const manifestQuery = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
	});

	const grouped = useMemo(() => {
		const releases = upstreamQuery.data?.releases ?? [];
		return releases.filter((r) => r.hasX64Deb);
	}, [upstreamQuery.data]);

	const catalogEmpty = !upstreamQuery.isPending && grouped.length === 0;

	const initialMode = (
		presetVersion ? "catalog" : catalogEmpty ? "latest" : "catalog"
	) as PublishChromiumMode;

	const form = useForm<PublishChromiumForm>({
		resolver: zodResolver(PublishChromiumFormSchema),
		defaultValues: {
			mode: initialMode,
			version: presetVersion,
		},
		mode: "onSubmit",
	});

	const mode = form.watch("mode");
	const version = form.watch("version");
	const [showAdvanced, setShowAdvanced] = useState(initialMode === "manual");

	useEffect(() => {
		if (catalogEmpty && mode === "catalog") {
			form.setValue("mode", "latest", { shouldDirty: false });
		}
	}, [catalogEmpty, mode, form]);

	useEffect(() => {
		if (mode === "manual") setShowAdvanced(true);
	}, [mode]);

	const publishedSet = useMemo(() => {
		const set = new Set<string>();
		const m = manifestQuery.data?.manifest?.browserMajors ?? {};
		for (const major of Object.values(m)) {
			for (const v of Object.keys(major.builds)) set.add(v);
		}
		return set;
	}, [manifestQuery.data]);

	const latestUpstream = useMemo(() => {
		const flat = grouped
			.slice()
			.sort(
				(a, b) =>
					new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
			);
		return flat[0] ?? null;
	}, [grouped]);

	const latestAlreadyPublished = !!(
		latestUpstream && publishedSet.has(latestUpstream.fullVersion)
	);

	// Resolve the version we'd actually publish for the current mode.
	// Manual mode only counts once the typed string is well-formed so
	// the disabled state doesn't flicker as the user types.
	const resolvedVersion = useMemo<string | null>(() => {
		if (mode === "latest") return latestUpstream?.fullVersion ?? null;
		if (mode === "catalog") return version || null;
		if (mode === "manual") {
			if (!version) return null;
			return FullVersionRegex.test(version) ? version : null;
		}
		return null;
	}, [mode, version, latestUpstream]);

	const resolvedUpstream = useMemo(() => {
		if (!resolvedVersion) return null;
		return (
			(upstreamQuery.data?.releases ?? []).find(
				(r) => r.fullVersion === resolvedVersion,
			) ?? null
		);
	}, [resolvedVersion, upstreamQuery.data]);

	const alreadyPublished =
		!!resolvedVersion && publishedSet.has(resolvedVersion);

	const submitMutation = useMutation({
		mutationFn: async (values: PublishChromiumForm) => {
			const body =
				values.mode === "latest"
					? { version: "latest" as const }
					: { version: values.version ?? "" };
			return unwrap(api.api.releases.chromium.post(body)) as Promise<{
				operationId: string;
			}>;
		},
		onSuccess: ({ operationId }) => {
			toast.success(
				mode === "latest"
					? "Downloading latest chromium…"
					: "Chromium release started",
			);
			navigate(`/operations/${operationId}`);
		},
		onError: (err) => {
			// Refresh the manifest so the disabled state reflects whatever
			// the server saw (the 409 path means the version snuck in
			// while the page was open).
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
			toast.error(err instanceof Error ? err.message : "Release failed");
		},
	});

	const onSubmit = form.handleSubmit((values) => {
		if (alreadyPublished) {
			toast.error("That chromium version is already published.");
			return;
		}
		submitMutation.mutate(values);
	});
	const submitting = submitMutation.isPending;

	const visibleModes = useMemo<ModeOption[]>(() => {
		const base: ModeOption[] = [
			{
				value: "catalog",
				title: "From catalog",
				description: catalogEmpty
					? "Mirror not yet populated."
					: `${grouped.length} version${grouped.length === 1 ? "" : "s"} synced ${
							upstreamQuery.data?.syncedAt
								? formatRelative(upstreamQuery.data.syncedAt)
								: ""
						}`,
				disabled: catalogEmpty,
			},
			{
				value: "latest",
				title: "Latest upstream",
				description: latestUpstream
					? `Will publish ${latestUpstream.fullVersion}`
					: "Resolved at run time from the upstream catalog.",
				disabled: latestAlreadyPublished,
				disabledReason: latestAlreadyPublished
					? `${latestUpstream?.fullVersion ?? "Latest"} is already in the manifest`
					: undefined,
			},
		];
		if (showAdvanced) {
			base.push({
				value: "manual",
				title: "Advanced (manual)",
				description: "Free-text major.x.y.z when nothing else fits.",
				disabled: false,
			});
		}
		return base;
	}, [
		catalogEmpty,
		grouped.length,
		upstreamQuery.data?.syncedAt,
		latestUpstream,
		latestAlreadyPublished,
		showAdvanced,
	]);

	const canSubmit = !submitting && !!resolvedVersion && !alreadyPublished;
	const showFooter = !!resolvedVersion;

	return (
		<motion.form
			onSubmit={onSubmit}
			noValidate
			initial={reduce ? false : { opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.32, ease: EASE }}
			className="flex w-full flex-col gap-8 pb-32"
		>
			<header className="flex flex-col gap-2">
				<span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
					Releases · Chromium
				</span>
				<h1 className="text-3xl font-semibold leading-tight tracking-tight">
					Publish chromium
				</h1>
				<p className="max-w-prose text-sm text-muted-foreground">
					Publish a patched Chromium build for a given major version. Pick from
					the upstream catalog or enter a version manually.
				</p>
			</header>

			<CatalogFreshnessStrip
				releases={upstreamQuery.data?.releases ?? []}
				syncedAt={upstreamQuery.data?.syncedAt ?? null}
				loading={upstreamQuery.isPending}
				canSync={canSync}
			/>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[20rem_minmax(0,1fr)]">
				<aside className="flex flex-col gap-5">
					<SourceRail
						mode={mode}
						onChangeMode={(v) =>
							form.setValue("mode", v, {
								shouldDirty: true,
								shouldValidate: false,
							})
						}
						options={visibleModes}
						showAdvanced={showAdvanced}
						onRevealAdvanced={() => setShowAdvanced(true)}
						releases={upstreamQuery.data?.releases ?? []}
						publishedSet={publishedSet}
						catalogValue={version}
						onCatalogChange={(v) =>
							form.setValue("version", v, {
								shouldDirty: true,
								shouldValidate: true,
							})
						}
						catalogDisabled={catalogEmpty}
						latestUpstream={latestUpstream}
						latestAlreadyPublished={latestAlreadyPublished}
						register={form.register}
						errors={form.formState.errors}
					/>
				</aside>

				<section className="min-w-0">
					<AnimatePresence mode="wait">
						{!resolvedVersion ? (
							<HeroEmpty />
						) : (
							<VersionHero
								key={resolvedVersion}
								resolvedVersion={resolvedVersion}
								upstream={resolvedUpstream}
								mode={mode}
								alreadyPublished={alreadyPublished}
							/>
						)}
					</AnimatePresence>
				</section>
			</div>

			<AnimatePresence>
				{showFooter ? (
					<CommitBar
						mode={mode}
						resolvedVersion={resolvedVersion}
						alreadyPublished={alreadyPublished}
						submitting={submitting}
						canSubmit={canSubmit}
					/>
				) : null}
			</AnimatePresence>
		</motion.form>
	);
}
